import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Typography
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import { CreateUpdateHealthInjectorCommand, HealthInjectorViewModel } from '../../models/device';
import { HealthSystemDomainHealthInjectorsDtosHealthInjectorManufacturerDto } from '../../api/HealthSystemApi';
import { FarmSelectModal } from '../../elements';
import { DeviceService } from '../../hooks';

interface CreateUpdateHealthInjectorProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
  mode: 'create' | 'update';
  injector: HealthInjectorViewModel | null;
}

const CreateUpdateHealthInjector: React.FC<any> = ({
  opened,
  onModalAction,
  mode,
  injector
}: CreateUpdateHealthInjectorProps) => {
  const classes = useStyles();
  const deviceService = new DeviceService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);
  const [macAddress, setMacAddress] = React.useState(injector?.macAddress || '');
  const [comment, setComment] = React.useState(injector?.comment || '');
  const [farm, setFarm] = React.useState(injector?.farm || null);

  // Manufacturer fields
  const [manufacturerModel, setManufacturerModel] = React.useState(injector?.manufacturer?.model || '');
  const [manufacturerMotorType, setManufacturerMotorType] = React.useState(injector?.manufacturer?.motorType || '');
  const [manufacturerDescription, setManufacturerDescription] = React.useState(
    injector?.manufacturer?.description || ''
  );
  const [manufacturerAluminumCoated, setManufacturerAluminumCoated] = React.useState(
    injector?.manufacturer?.aluminumCoated || false
  );
  const [manufacturerPcbType, setManufacturerPcbType] = React.useState(injector?.manufacturer?.pcbType || '');
  const [manufacturerRssiDbm, setManufacturerRssiDbm] = React.useState<number | null>(
    injector?.manufacturer?.rssiDbm ?? null
  );

  React.useEffect(() => {
    if (injector) {
      setMacAddress(injector?.macAddress || '');
      setFarm(injector?.farm || null);
      setComment(injector?.comment || '');
      // Manufacturer fields
      setManufacturerModel(injector?.manufacturer?.model || '');
      setManufacturerMotorType(injector?.manufacturer?.motorType || '');
      setManufacturerDescription(injector?.manufacturer?.description || '');
      setManufacturerAluminumCoated(injector?.manufacturer?.aluminumCoated || false);
      setManufacturerPcbType(injector?.manufacturer?.pcbType || '');
      setManufacturerRssiDbm(injector?.manufacturer?.rssiDbm ?? null);
    }
  }, [injector]);

  React.useEffect(() => {
    if (!injector) {
      clearFields();
    }
  }, [opened]);

  const closeDialog = (text: string | null) => {
    if (text == null) {
      clearFields();
      onModalAction(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (mode === 'create') {
      handleCreateInjector();
    } else if (mode === 'update') {
      handleUpdateInjector();
    }

    clearFields();
  };

  const handleCreateInjector = () => {
    setIsBusy(true);

    deviceService
      .createHelathInjector(buildInjectorObject())
      .then(() => {
        notifications.success(t('f-injectors-add-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleUpdateInjector = () => {
    setIsBusy(true);

    deviceService
      .updateHelathInjector(buildInjectorObject())
      .then(() => {
        notifications.success(t('f-injectors-upd-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildInjectorObject = () => {
    const manufacturer: HealthSystemDomainHealthInjectorsDtosHealthInjectorManufacturerDto = {
      model: manufacturerModel || null,
      motorType: manufacturerMotorType || null,
      description: manufacturerDescription || null,
      aluminumCoated: manufacturerAluminumCoated,
      pcbType: manufacturerPcbType || null,
      rssiDbm: manufacturerRssiDbm
    };

    return {
      macAddress,
      comment,
      farmId: farm?.id || null,
      id: injector?.id || null,
      manufacturer
    } as CreateUpdateHealthInjectorCommand;
  };

  const clearFields = () => {
    setMacAddress('');
    setFarm(null);
    setComment('');
    // Clear manufacturer fields
    setManufacturerModel('');
    setManufacturerMotorType('');
    setManufacturerDescription('');
    setManufacturerAluminumCoated(false);
    setManufacturerPcbType('');
    setManufacturerRssiDbm(null);
  };

  const validateForm = (): boolean => {
    if (!macAddress) {
      notifications.warning(t('f-val-inj-mac'));
      return false;
    }

    return true;
  };

  return (
    <Dialog
      id="injectorDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="sm"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="injectorDialogTitle" className={classes.dialogHeaderFooter}>
        {mode === 'create' ? t('f-injectors-add-title') : t('f-injectors-upd-title')}
      </DialogTitle>

      <DialogContent>
        <Grid item container md={12} direction="column">
          <Grid item>
            <TextField
              label={t('f-logs-mac')}
              size="small"
              fullWidth
              variant="outlined"
              disabled={mode === 'update'}
              className={classes.formControl}
              value={macAddress}
              onChange={(e) => {
                setMacAddress(e.target.value);
              }}
            />
          </Grid>

          <Grid item>
            <FarmSelectModal disableAny farm={farm} onChange={(farm) => setFarm(farm)} sx={{ m: 1 }} />
          </Grid>

          <Grid item>
            <TextField
              label={t('m-global-comments')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
              }}
            />
          </Grid>

          {/* Manufacturer Section */}
          <Grid item>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              {t('f-injector-manufacturer')}
            </Typography>
          </Grid>

          <Grid item>
            <TextField
              label={t('f-injector-model')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={manufacturerModel}
              onChange={(e) => setManufacturerModel(e.target.value)}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-injector-motor-type')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={manufacturerMotorType}
              onChange={(e) => setManufacturerMotorType(e.target.value)}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-injector-description')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={manufacturerDescription}
              onChange={(e) => setManufacturerDescription(e.target.value)}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-injector-signal-strength')}
              size="small"
              fullWidth
              variant="outlined"
              type="number"
              className={classes.formControl}
              value={manufacturerRssiDbm ?? ''}
              onChange={(e) => setManufacturerRssiDbm(e.target.value ? parseInt(e.target.value, 10) : null)}
              InputProps={{
                endAdornment: <span className="text-gray-500 text-sm">dBm</span>
              }}
            />
          </Grid>

          <Grid item>
            <TextField
              select
              label={t('f-injector-pcb-type')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={manufacturerPcbType}
              onChange={(e) => setManufacturerPcbType(e.target.value)}
            >
              <MenuItem value="">{t('f-com-none')}</MenuItem>
              <MenuItem value="Nano">Nano</MenuItem>
              <MenuItem value="Chainway">Chainway</MenuItem>
            </TextField>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Checkbox
                  checked={manufacturerAluminumCoated}
                  onChange={(e) => setManufacturerAluminumCoated(e.target.checked)}
                  color="primary"
                />
              }
              label={t('f-injector-aluminum-coated')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => closeDialog(null)}
          variant="outlined"
          disabled={isBusy}
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={() => closeDialog('ok')}
          id="saveUserBtn"
          disabled={isBusy}
          startIcon={<Icon>{'save'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateHealthInjector;
